import { useCallback, useMemo } from 'react';

import { type GlobalShortcutsItem } from '@atlassian/global-shortcuts';

import { useItemsAndShortcuts } from '../../../controllers/items-and-shortcuts-provider';
import { useOptions } from '../../../controllers/options-provider';
import { useUserMenuDataShapes } from '../../../controllers/user-state/user-menu-data-shapes-provider';
import { INTERNAL_GLOBAL_ITEMS } from '../../../ui/more-menu/constants';
import { type SideNavViewedSideNavAppShortcutsSectionAttributesType } from '../../analytics/generated/analytics.types';
import { useAnalyticsEvents } from '../../analytics/generated/use-analytics-events';

export function useNavAnalytics() {
	const { fireEvent } = useAnalyticsEvents();
	const { disableMenuCustomization, hasBottomElement } = useOptions();
	const { allItems, globalItems, growthItemShortcuts, items, shortcuts } = useItemsAndShortcuts();

	const {
		hiddenMenuIds,
		hiddenItemMenuIds,
		hiddenShortcutMenuIds,
		visibleMenuIds,
		visibleItemMenuIds,
		visibleShortcutMenuIds,
	} = useUserMenuDataShapes();

	// Global items + customize/more + bottom element
	const globalItemsList = useMemo(
		() => [
			...visibleItemMenuIds.filter((id) => globalItems.has(id)),
			...(!disableMenuCustomization
				? [
						!!hiddenItemMenuIds.length || !!hiddenShortcutMenuIds.length
							? INTERNAL_GLOBAL_ITEMS.more
							: INTERNAL_GLOBAL_ITEMS.customize,
					]
				: []),
			...(hasBottomElement ? [INTERNAL_GLOBAL_ITEMS.bottomElem] : []),
		],
		[
			disableMenuCustomization,
			hasBottomElement,
			hiddenItemMenuIds,
			hiddenShortcutMenuIds,
			globalItems,
			visibleItemMenuIds,
		],
	);

	// App specific items
	const appSpecificItemsList = useMemo(
		() => visibleItemMenuIds.filter((id) => allItems[id] && !globalItems.has(id)),
		[allItems, globalItems, visibleItemMenuIds],
	);

	// Shortcuts + growth shortcuts
	const shortcutIds = useMemo(() => Object.keys(growthItemShortcuts ?? {}), [growthItemShortcuts]);
	const shortcutItemList = useMemo(
		() => [...visibleShortcutMenuIds, ...shortcutIds],
		[visibleShortcutMenuIds, shortcutIds],
	);

	// All visible items
	const navItemList = useMemo(
		() => [...globalItemsList, ...appSpecificItemsList, ...shortcutItemList],
		[globalItemsList, appSpecificItemsList, shortcutItemList],
	);

	// Item group
	const itemsIds = useMemo(() => Object.keys(items), [items]);
	const getItemGroup = useCallback(
		(id: string) => {
			return !!growthItemShortcuts?.[id] || !!shortcuts?.[id]
				? 'appShortcut'
				: itemsIds.includes(id)
					? 'appSpecific'
					: 'global';
		},
		[growthItemShortcuts, itemsIds, shortcuts],
	);

	const getGroupItems = useCallback(
		(group: string) => {
			switch (group) {
				case 'global':
					return globalItemsList;
				case 'appSpecific':
					return appSpecificItemsList;
				case 'appShortcut':
					return shortcutItemList;
				default:
					return [];
			}
		},
		[appSpecificItemsList, globalItemsList, shortcutItemList],
	);

	const fireViewedSideNavEvent = useCallback(() => {
		const moreMenuItems = hiddenItemMenuIds.concat(hiddenShortcutMenuIds);

		const globalItemsList = [
			...globalItems.keys(),
			...(!disableMenuCustomization
				? [moreMenuItems.length ? INTERNAL_GLOBAL_ITEMS.more : INTERNAL_GLOBAL_ITEMS.customize]
				: []),
			...(hasBottomElement ? [INTERNAL_GLOBAL_ITEMS.bottomElem] : []),
		];

		const appSpecificItemsList = Object.keys(items);
		const appShortcutItemsList = visibleShortcutMenuIds;

		fireEvent('ui.sideNav.viewed.sideNavMenu', {
			globalItemsCount: globalItemsList.length,
			globalItemsList,
			appSpecificItemsCount: appSpecificItemsList.length,
			appSpecificItemsList,
			appShortcutItemsCount: appShortcutItemsList.length,
			appShortcutItemsList,
			inMoreItemsCount: moreMenuItems.length,
			inMoreItemsList: moreMenuItems,
		});
	}, [
		hiddenItemMenuIds,
		hiddenShortcutMenuIds,
		globalItems,
		disableMenuCustomization,
		hasBottomElement,
		items,
		visibleShortcutMenuIds,
		fireEvent,
	]);

	const fireClickedSideNavItemEvent = useCallback(
		(
			id: string,
			navLayoutComponent?: 'main' | 'preMain' | 'postMain' | 'bottom',
			isRightClick: boolean = false,
		) => {
			// Assign the current group based on the item type
			const currentGroup = getItemGroup(id);
			const isInternalGlobalItem = Object.values(INTERNAL_GLOBAL_ITEMS).includes(id);
			// Get visible items based on their group
			const visibleGroupItems = getGroupItems(currentGroup);

			const isInMore =
				// customize is in more if there are any hidden items
				(INTERNAL_GLOBAL_ITEMS.customize === id && !!hiddenMenuIds.length) ||
				hiddenMenuIds.includes(id);

			// Current index irrespective of the item group
			const currentIndex = isInMore
				? -1
				: // More and cusomise and growth shortcuts are always at the end of the list
					isInternalGlobalItem || !!growthItemShortcuts?.[id]
					? visibleMenuIds.length
					: visibleMenuIds.indexOf(id);

			// Current index within the item group
			const currentGroupIndex = isInMore ? -1 : visibleGroupItems.indexOf(id);

			const currentComponent = navLayoutComponent
				? navLayoutComponent
				: isInMore
					? 'postMain'
					: 'main';

			fireEvent('ui.sideNav.clicked.sideNavItem', {
				navLayoutComponent: currentComponent,
				navItemsCount: navItemList.length,
				navItemsIndex: currentIndex,
				group: currentGroup,
				groupItemsCount: visibleGroupItems.length,
				groupItemsIndex: currentGroupIndex,
				groupItem: id,
				isInMore,
				isRightClick,
			});
		},
		[
			fireEvent,
			getGroupItems,
			getItemGroup,
			growthItemShortcuts,
			hiddenMenuIds,
			navItemList.length,
			visibleMenuIds,
		],
	);

	const fireDraggedSideNavItemEvent = useCallback(
		(id: string, isInMore: boolean = false) => {
			// Assign the current group based on the item type
			const currentGroup = getItemGroup(id);
			// Get visible items based on their group
			const visibleGroupItems = getGroupItems(currentGroup);

			// Current index irrespective of the item group
			const currentIndex = isInMore
				? -1
				: // growth shortcuts are always at the end of the list
					!!growthItemShortcuts?.[id]
					? visibleMenuIds.length
					: visibleMenuIds.indexOf(id);

			// Current index within the item group
			const currentGroupIndex = isInMore ? -1 : visibleGroupItems.indexOf(id);

			const currentComponent = isInMore ? 'postMain' : 'main';

			fireEvent('ui.sideNav.dragged.sideNavItem', {
				navLayoutComponent: currentComponent,
				navItemsCount: navItemList.length,
				navItemsIndex: currentIndex,
				group: currentGroup,
				groupItemsCount: visibleGroupItems.length,
				groupItemsIndex: currentGroupIndex,
				groupItem: id,
				isInMore,
			});
		},
		[
			fireEvent,
			getGroupItems,
			getItemGroup,
			growthItemShortcuts,
			navItemList.length,
			visibleMenuIds,
		],
	);

	const fireViewedSideNavSubMenuEvent = useCallback(
		(
			id: string,
			subItemsCount?: number,
			isInMore: boolean = false,
			navLayoutComponent?: 'main' | 'preMain' | 'postMain' | 'bottom',
		) => {
			// Assign the current group based on the item type
			const currentGroup = getItemGroup(id);

			// Get visible items based on their group
			const visibleGroupItems = getGroupItems(currentGroup);

			const currentComponent = navLayoutComponent
				? navLayoutComponent
				: isInMore
					? 'postMain'
					: 'main';

			fireEvent('ui.sideNav.viewed.sideNavSubMenu', {
				group: currentGroup,
				groupItem: id,
				groupItemsCount: visibleGroupItems.length,
				groupItemsIndex: visibleGroupItems.indexOf(id),
				groupSubItemCount: subItemsCount ?? null,
				isInMore,
				navLayoutComponent: currentComponent,
			});
		},
		[fireEvent, getGroupItems, getItemGroup],
	);

	const fireViewedShortcutsEvent = useCallback(
		(shortcuts: GlobalShortcutsItem[]) => {
			const attributes: SideNavViewedSideNavAppShortcutsSectionAttributesType = {
				appShortcutItemsCount: shortcuts.length,
				appShortcutItemsList: shortcuts.map((item) => item.label),
			};
			fireEvent('ui.sideNav.viewed.sideNavAppShortcutsSection', attributes);
		},
		[fireEvent],
	);

	const fireClickedSideNavSubMenuItemEvent = useCallback(
		(
			id: string,
			subItemsCount?: number,
			isInMore: boolean = false,
			isRightClick: boolean = false,
			navLayoutComponent?: 'main' | 'preMain' | 'postMain' | 'bottom',
		) => {
			// Assign the current group based on the item type
			const currentGroup = getItemGroup(id);

			// Get visible items based on their group
			const visibleGroupItems = getGroupItems(currentGroup);

			const currentComponent = navLayoutComponent
				? navLayoutComponent
				: isInMore
					? 'postMain'
					: 'main';

			fireEvent('ui.sideNav.clicked.sideNavSubItem', {
				group: currentGroup,
				groupItem: id,
				groupItemsCount: visibleGroupItems.length,
				groupItemsIndex: visibleGroupItems.indexOf(id),
				groupSubItem: null,
				groupSubItemCount: subItemsCount ?? null,
				groupSubItemIndex: null,
				isInMore,
				isRightClick,
				navLayoutComponent: currentComponent,
			});
		},
		[fireEvent, getGroupItems, getItemGroup],
	);

	return {
		fireClickedSideNavItemEvent,
		fireClickedSideNavSubMenuItemEvent,
		fireDraggedSideNavItemEvent,
		fireViewedShortcutsEvent,
		fireViewedSideNavEvent,
		fireViewedSideNavSubMenuEvent,
	};
}
